import {
	Box,
	Button,
	Inline,
	Icons,
	Stack,
	Heading,
	Text,
	Col,
	Component,
	Divider,
	Grid,
	Select,
} from "@sembark-travel/ui-base"
import { Dialog } from "@sembark-travel/ui-dialog"
import {
	timeToUTCString,
	parseDate,
	formatDate,
} from "@sembark-travel/datetime-utils"
import {
	DatePickerField,
	SelectField,
	TextInputField,
	withServerErrors,
	SubmissionError,
	Form,
	FieldArray,
	arrayMutators,
	validateFormValues,
	PincodeValidator,
	EmptyNumberValidator,
	getEditablePhoneNumber,
	CheckboxInputField,
	GetFieldValue,
	DateRangePickerField,
	useForm,
	SwitchInputField,
} from "@sembark-travel/ui-form"
import { useXHR } from "@sembark-travel/xhr"
import { useCallback, useRef } from "react"
import { Optional, Required } from "utility-types"
import * as Validator from "yup"
import { AddressInputField, TAddressInputFieldValue } from "../Addresses"
import config from "../config"
import { SelectHotelGroup } from "../HotelGroups"
import { IHotelGroup } from "../HotelGroups"
import { SelectMealPlans, IMealPlan } from "./../MealPlans"
import { SelectRoomTypes, IRoomType } from "./../RoomTypes"
import {
	IHotel,
	IHotelRoomType,
	TBlackoutRoomInterval,
	THotelLocation,
	TSoldoutRoomInterval,
} from "./store"
import { SelectTripDestination, TTripDestination } from "../TripDestinations"
import { useAuthUser } from "../Auth"
import { SelectHotelLocation } from "./Locations"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import {
	IPaymentPreference,
	SelectHotelPaymentPreferences,
} from "../SupplierPaymentPreferences"

interface NewItemCredentials {
	name: string
	location?: THotelLocation
	group?: IHotelGroup
	stars?: string
	extra_bed_child_age_start: number
	extra_bed_child_age_end: number
	meal_plans: IMealPlan[]
	room_types?: {
		room_types: IRoomType[]
		allowed_extra_beds: number
		allowed_adults_with_extra_bed: number
		allowed_children_with_extra_bed: number
		allowed_children_without_extra_bed?: number
		no_of_rooms?: number
	}[]
	address: TAddressInputFieldValue
	payment_preference?: IPaymentPreference
	checkin_at: string | Date
	checkout_at: string | Date
	trip_destinations?: Array<TTripDestination>
	url?: string
	quick_add?: boolean
	same_black_out_date_intervals_for_all_room_types: boolean
	same_sold_out_date_intervals_for_all_room_types: boolean
	stop_sale_date_intervals: Array<{ start_date?: Date; end_date?: Date }>
	blackout_date_intervals: Array<{ start_date?: Date; end_date?: Date }>
	stop_sale_date_for_room_types: TSoldoutRoomInterval[]
	blackout_date_for_room_types: TBlackoutRoomInterval[]
}
const defaultInitialValues: Required<NewItemCredentials, "room_types"> = {
	name: "",
	location: undefined,
	group: undefined,
	stars: undefined,
	extra_bed_child_age_start: 6,
	extra_bed_child_age_end: 12,
	meal_plans: [],
	room_types: [
		{
			room_types: [],
			allowed_extra_beds: 0,
			allowed_adults_with_extra_bed: 1,
			allowed_children_with_extra_bed: 1,
			allowed_children_without_extra_bed: undefined,
			no_of_rooms: undefined,
		},
	],
	address: {
		line_1: "",
		line_2: "",
		landmark: "",
		postcode: "",
		phone_numbers: [
			{
				country_code: "IN",
				phone_number: "+91",
				number: "",
			},
		],
		email: "",
		location: undefined,
		city: undefined,
		state: undefined,
		country: undefined,
		type: "",
	},
	checkin_at: parseDate(config.defaultCheckinAt, config.timeFormat),
	checkout_at: parseDate(config.defaultCheckoutAt, config.timeFormat),
	trip_destinations: [],
	url: "",
	same_black_out_date_intervals_for_all_room_types: true,
	same_sold_out_date_intervals_for_all_room_types: true,
	stop_sale_date_intervals: [],
	blackout_date_intervals: [],
	stop_sale_date_for_room_types: [],
	blackout_date_for_room_types: [],
}

export interface NewItemProps {
	onSubmit: (values: unknown) => Promise<IHotel>
	onCancel?: () => void
	initialValues?: Optional<NewItemCredentials>
	showFullAddress?: boolean
	isEditing?: boolean
}

export function CreateHotel({
	onSuccess,
	...props
}: Omit<NewItemProps, "onSubmit"> & {
	onSuccess: (hotel: IHotel) => void
}) {
	const xhr = useXHR()
	const handleSubmit = useCallback(
		async (data: unknown): Promise<IHotel> => {
			const resp = await xhr.post("/hotels", data)
			const hotel = resp.data.data
			onSuccess && onSuccess(hotel)
			return hotel
		},
		[xhr, onSuccess]
	)
	return <NewHotelForm {...props} onSubmit={handleSubmit} />
}

export function NewHotelForm({
	onCancel,
	onSubmit,
	initialValues: propInitialValues = {},
	showFullAddress,
	isEditing = false,
}: NewItemProps) {
	const { user } = useAuthUser()
	const can_attach_services_to_destinations = Boolean(
		user?.tenant?.feature_flags?.attach_services_to_destinations
	)
	const { name, trip_destinations, ...restData } = propInitialValues
	const initialValues = useRef({
		...defaultInitialValues,
		...propInitialValues,
		trip_destinations: !can_attach_services_to_destinations
			? undefined
			: propInitialValues.trip_destinations ||
				defaultInitialValues.trip_destinations,
		address: {
			...defaultInitialValues.address,
			...propInitialValues.address,
			phone_numbers: propInitialValues.address?.phone_numbers
				? propInitialValues.address.phone_numbers.map((p) =>
						getEditablePhoneNumber(p.phone_number, p.country_code)
					)
				: defaultInitialValues.address.phone_numbers,
		},
		checkin_at: parseDate(
			propInitialValues.checkin_at
				? propInitialValues.checkin_at
				: propInitialValues.trip_destinations?.[0]?.hotel_checkin_at ||
						defaultInitialValues.checkin_at,
			config.timeFormat
		),

		checkout_at: parseDate(
			propInitialValues.checkout_at
				? propInitialValues.checkout_at
				: propInitialValues.trip_destinations?.[0]?.hotel_checkout_at ||
						defaultInitialValues.checkout_at,
			config.timeFormat
		),
		quick_add: !Object.values(restData).length,
		extra_bed_child_age_start: propInitialValues.extra_bed_child_age_start
			? propInitialValues.extra_bed_child_age_start
			: (propInitialValues.trip_destinations || []).length
				? propInitialValues?.trip_destinations?.[0].min_child_age
				: defaultInitialValues.extra_bed_child_age_start,
		extra_bed_child_age_end: propInitialValues.extra_bed_child_age_end
			? propInitialValues.extra_bed_child_age_end
			: (propInitialValues.trip_destinations || []).length
				? propInitialValues?.trip_destinations?.[0].max_child_age
				: defaultInitialValues.extra_bed_child_age_end,
		stop_sale_date_intervals:
			propInitialValues.stop_sale_date_intervals ??
			defaultInitialValues.stop_sale_date_intervals,
		blackout_date_intervals:
			propInitialValues.blackout_date_intervals ??
			defaultInitialValues.blackout_date_intervals,
		same_black_out_date_intervals_for_all_room_types:
			propInitialValues.same_black_out_date_intervals_for_all_room_types ??
			defaultInitialValues.same_black_out_date_intervals_for_all_room_types,
		same_sold_out_date_intervals_for_all_room_types:
			propInitialValues.same_sold_out_date_intervals_for_all_room_types ??
			defaultInitialValues.same_sold_out_date_intervals_for_all_room_types,
		stop_sale_date_for_room_types:
			propInitialValues.stop_sale_date_for_room_types ??
			defaultInitialValues.stop_sale_date_for_room_types,
		blackout_date_for_room_types:
			propInitialValues.blackout_date_for_room_types ??
			defaultInitialValues.blackout_date_for_room_types,
	})
	const validationSchema = Validator.object().shape({
		name: Validator.string().required("Name field is required"),
		quick_add: Validator.boolean(),
		location: Validator.mixed()
			.nullable()
			.required("Please provide location for hotel"),
		group: Validator.mixed().nullable(),
		stars: Validator.string()
			.nullable()
			.max(35, "Please use 35 or fewer characters"),
		extra_bed_child_age_start: Validator.number()
			.positive("Child start age should be a positive number")
			.integer("Child start age should be an integer")
			.required("Child start age is required"),
		extra_bed_child_age_end: Validator.number()
			.positive("Child end age should be a positive number")
			.integer("Child end age should be an integer")
			.required("Child end age is required"),
		meal_plans: Validator.array().min(1, "Please select atleast one meal plan"),
		room_types: Validator.array()
			.of(
				Validator.object().shape({
					room_types: Validator.array()
						.min(1, "Please select atleast one room type")
						.required("Please select a room type"),
					allowed_extra_beds: Validator.number()
						.typeError("Allowed extra bed must be a number")
						.integer("Allowed extra beds should be an integer")
						.min(0, "Allowed extra beds should not be negative")
						.max(200, "Can not be greater then 200")
						.required("Allowed extra beds field is required"),
					allowed_adults_with_extra_bed: EmptyNumberValidator()
						.integer("Allowed adult extra beds per room should be an integer")
						.min(0, "Allowed adult extra beds should not be negative")
						.max(200, "Can not be greater then 200"),
					allowed_children_with_extra_bed: EmptyNumberValidator()
						.integer("Allowed child extra beds per room should be an integer")
						.min(0, "Allowed child extra beds should not be negative")
						.max(200, "Can not be greater then 200"),
					allowed_children_without_extra_bed: EmptyNumberValidator()
						.integer("Allowed CNBs per room should be an integer")
						.min(0, "Allowed CNBs per room should not be negative")
						.max(200, "Can not be greater then 200"),
					no_of_rooms: EmptyNumberValidator()
						.nullable()
						.positive("Please provide a positive value.")
						.integer("Please provide a positive integer value"),
				})
			)
			.min(1, "Please select atleast one room type"),
		trip_destinations: can_attach_services_to_destinations
			? Validator.array()
					.required("Please select a Destinations")
					.min(1, "Please select atleast one Destinations")
			: Validator.array().nullable(),
		address: Validator.object().shape({
			line_1: Validator.string()
				.nullable()
				.max(100, "Please use 100 or less characters."),
			line_2: Validator.string()
				.nullable()
				.max(100, "Please use 100 or less characters."),
			landmark: Validator.string()
				.nullable()
				.max(100, "Please use 100 or less characters."),
			postcode: PincodeValidator().nullable(),
			email: Validator.string().email("Please enter a valid email").nullable(),
		}),
		same_black_out_date_intervals_for_all_room_types: Validator.boolean(),
		same_sold_out_date_intervals_for_all_room_types: Validator.boolean(),
		url: Validator.string().nullable(),
		blackout_date_intervals: Validator.array().of(
			Validator.object().shape({
				start_date: Validator.date()
					.nullable()
					.when("end_date", {
						is: (end_date: Date | undefined) => !end_date,
						then: (schema) =>
							schema.required("Please select a start or end date"),
						otherwise: (schema) => schema.notRequired(),
					}),
				end_date: Validator.date().nullable(),
			})
		),
		stop_sale_date_intervals: Validator.array().of(
			Validator.object().shape({
				start_date: Validator.date()
					.nullable()
					.when("end_date", {
						is: (end_date: Date | undefined) => !end_date,
						then: (schema) =>
							schema.required("Please select a start or end date"),
						otherwise: (schema) => schema.notRequired(),
					}),
				end_date: Validator.date().nullable(),
			})
		),
		// -------- per-room-type cards (used when "same for all" is FALSE) --------
		stop_sale_date_for_room_types: Validator.array().of(
			Validator.object().shape({
				room_types: Validator.array()
					.min(1, "Please select atleast one room type")
					.required("Please select a room type"),
				black_out_date_intervals: Validator.array()
					.of(
						Validator.object().shape({
							start_date: Validator.date()
								.nullable()
								.when("end_date", {
									is: (end_date: Date | undefined) => !end_date,
									then: (schema) =>
										schema.required("Please select a start or end date"),
									otherwise: (schema) => schema.notRequired(),
								}),
							end_date: Validator.date().nullable(),
						})
					)
					.min(1, "Please add atleast one date interval"),
			})
		),

		blackout_date_for_room_types: Validator.array().of(
			Validator.object().shape({
				room_types: Validator.array()
					.min(1, "Please select atleast one room type")
					.required("Please select a room type"),
				black_out_date_intervals: Validator.array()
					.of(
						Validator.object().shape({
							start_date: Validator.date()
								.nullable()
								.when("end_date", {
									is: (end_date: Date | undefined) => !end_date,
									then: (schema) =>
										schema.required("Please select a start or end date"),
									otherwise: (schema) => schema.notRequired(),
								}),
							end_date: Validator.date().nullable(),
						})
					)
					.min(1, "Please add atleast one date interval"),
			})
		),
	})
	const validate = validateFormValues(validationSchema)
	return (
		<Form<NewItemCredentials>
			initialValues={initialValues.current}
			validate={validate}
			mutators={{ ...arrayMutators }}
			onSubmit={withServerErrors(async (values) => {
				const {
					meal_plans,
					room_types,
					address,
					payment_preference,
					checkin_at,
					checkout_at,
					group,
					trip_destinations,
					location,
					name,
					stars,
					extra_bed_child_age_start,
					extra_bed_child_age_end,
					url,
				} = values
				const data = {
					name,
					stars,
					extra_bed_child_age_end,
					extra_bed_child_age_start,
					url,
					location: location?.name,
					group: group ? group.name : undefined,
					meal_plans: meal_plans.map((mealPlan) => mealPlan.name),
					room_types:
						room_types?.reduce(
							(
								rooms: Array<{
									name: string
									allowed_extra_beds: number
									allowed_adults_with_extra_bed: number
									allowed_children_with_extra_bed: number
									allowed_children_without_extra_bed?: number
									no_of_rooms?: number
									black_out_date_intervals: [string, string, number][]
								}>,
								{
									room_types,
									allowed_extra_beds,
									no_of_rooms,
									allowed_adults_with_extra_bed,
									allowed_children_with_extra_bed,
									allowed_children_without_extra_bed,
								}
							) =>
								rooms.concat(
									room_types.map((room_type) => {
										const intervals: [string, string, number][] = []
										if (
											values.same_sold_out_date_intervals_for_all_room_types
										) {
											if (values.stop_sale_date_intervals?.length) {
												values.stop_sale_date_intervals
													.filter(
														(interval) =>
															interval?.start_date || interval?.end_date
													)
													.forEach((interval) => {
														const start =
															interval.start_date ?? interval.end_date
														const end = interval.end_date ?? interval.start_date
														if (!start || !end) return
														intervals.push([
															formatDate(start, "YYYY-MM-DD"),
															formatDate(end, "YYYY-MM-DD"),
															1,
														])
													})
											}
										} else {
											values.stop_sale_date_for_room_types
												?.filter((item) =>
													item.room_types?.some(
														(rt) => Number(rt.value) === room_type.id
													)
												)
												.forEach((item) => {
													item.black_out_date_intervals
														?.filter(
															(interval) =>
																interval.start_date || interval.end_date
														)
														.forEach((interval) => {
															const start =
																interval.start_date ?? interval.end_date
															const end =
																interval.end_date ?? interval.start_date
															if (!start || !end) return
															intervals.push([
																formatDate(start, "YYYY-MM-DD"),
																formatDate(end, "YYYY-MM-DD"),
																1,
															])
														})
												})
										}

										if (
											values.same_black_out_date_intervals_for_all_room_types
										) {
											if (values.blackout_date_intervals?.length) {
												values.blackout_date_intervals
													.filter(
														(interval) =>
															interval?.start_date || interval?.end_date
													)
													.forEach((interval) => {
														const start =
															interval.start_date ?? interval.end_date
														const end = interval.end_date ?? interval.start_date
														if (!start || !end) return
														intervals.push([
															formatDate(start, "YYYY-MM-DD"),
															formatDate(end, "YYYY-MM-DD"),
															0,
														])
													})
											}
										} else {
											values.blackout_date_for_room_types
												?.filter((item) =>
													item.room_types?.some(
														(rt) => Number(rt.value) === room_type.id
													)
												)
												.forEach((item) => {
													item.black_out_date_intervals
														?.filter(
															(interval) =>
																interval.start_date || interval.end_date
														)
														.forEach((interval) => {
															const start =
																interval.start_date ?? interval.end_date
															const end =
																interval.end_date ?? interval.start_date
															if (!start || !end) return
															intervals.push([
																formatDate(start, "YYYY-MM-DD"),
																formatDate(end, "YYYY-MM-DD"),
																0,
															])
														})
												})
										}
										return {
											name: room_type.name,
											allowed_extra_beds,
											allowed_adults_with_extra_bed,
											allowed_children_with_extra_bed,
											allowed_children_without_extra_bed,
											no_of_rooms,
											black_out_date_intervals: intervals,
										}
									})
								),
							[]
						) || [],
					address: {
						...address,
						phone_numbers: address?.phone_numbers
							?.filter((p) => p.number)
							.map((p) => ({
								...p,
								number: String(p.number).replace(/[^\d]/gi, ""),
							})),
						location: location?.name,
						city: address.city?.name,
						state: address.state?.name,
						country: address.country?.name,
					},
					payment_preference_id: payment_preference
						? payment_preference.id
						: undefined,
					checkin_at: timeToUTCString(checkin_at),
					checkin_at_local: formatDate(
						parseDate(checkin_at, config.timeFormat),
						config.timeFormat
					),
					checkout_at: timeToUTCString(checkout_at),
					checkout_at_local: formatDate(
						parseDate(checkout_at, config.timeFormat),
						config.timeFormat
					),
					trip_destinations: (trip_destinations || []).map((t) => t.id),
				}
				return await onSubmit(data)
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit, form }) => {
				return (
					<form noValidate onSubmit={handleSubmit}>
						<GetFieldValue<boolean> name={`quick_add`}>
							{({ value: quickAdd }) => (
								<>
									<Grid>
										<Col sm={12} md={{ offset: 4, span: 8 }}>
											<Inline paddingBottom="2" justifyContent="end">
												<Box
													paddingX={"2"}
													paddingY={"1"}
													borderRadius="md"
													borderWidth={"2"}
													borderColor={"primary"}
													bgColor={quickAdd ? "primary" : "accent"}
												>
													<CheckboxInputField
														label={"Quick Add"}
														name={`quick_add`}
													/>
												</Box>
											</Inline>
										</Col>
									</Grid>
									<Grid gap="4">
										<Col sm={12} md={4}>
											<Stack gap="1">
												<Heading as="h3">Basic Details</Heading>
												<Text color="muted">
													Provide basic details e.g. name, star rating and
													address.
												</Text>
											</Stack>
										</Col>
										<Col>
											<Stack gap="4">
												<Grid gap="4">
													<Col xs={12} sm={6} lg>
														<TextInputField
															label="Name"
															name="name"
															placeholder="Taj Hotel"
															required
															type="text"
														/>
													</Col>
													{!quickAdd ? (
														<Col>
															<SelectField
																select={SelectHotelGroup}
																label="Group Name"
																secondaryLabel="optional"
																name="group"
																creatable
															/>
														</Col>
													) : null}
													<Col>
														<TextInputField
															label="Stars"
															name="stars"
															type="text"
															required
														/>
													</Col>
												</Grid>
												<SelectField
													label="Location"
													placeholder="e.g. City, State, Name"
													name="location"
													select={SelectHotelLocation}
													help="Hotel's city location. Must match with uploaded data. This will used during filters and quote sharing"
													creatable
												/>

												{can_attach_services_to_destinations ? (
													<SelectField
														select={SelectTripDestination}
														name="trip_destinations"
														label="Trip Destinations"
														multiple
														fetchOnMount
														onChange={(val: Array<TTripDestination>) => {
															if (val) {
																form.batch(() => {
																	form.change("trip_destinations", [...val])
																	if (val.length > 0 && !isEditing) {
																		if (val[0].hotel_checkin_at) {
																			form.change(
																				"checkin_at",
																				parseDate(
																					val[0].hotel_checkin_at,
																					"HH:mm"
																				)
																			)
																		}
																		if (val[0].hotel_checkout_at) {
																			form.change(
																				"checkout_at",
																				parseDate(
																					val[0].hotel_checkout_at,
																					"HH:mm"
																				)
																			)
																		}
																		form.change(
																			"extra_bed_child_age_start",
																			val[0].min_child_age
																		)
																		form.change(
																			"extra_bed_child_age_end",
																			val[0].max_child_age
																		)
																	}
																})
															}
														}}
													/>
												) : null}
												{quickAdd ? null : (
													<Component initialState={showFullAddress || false}>
														{({ state, setState }) => (
															<Stack gap="4">
																{!state ? (
																	<Stack gap="4">
																		<Stack gap="2">
																			<Inline gap="4" alignItems="center">
																				<Text fontWeight="semibold">
																					Full Address
																				</Text>
																				<Button
																					onClick={() => setState(!state)}
																					size="sm"
																					level="tertiary"
																				>
																					{state ? (
																						<Icons.ChevronDown
																							rotate="180"
																							title="Hide Address"
																						/>
																					) : (
																						<Icons.ChevronDown title="Show Address" />
																					)}
																				</Button>
																			</Inline>
																			<Text color="muted">
																				This will be used when generating
																				vouchers.
																			</Text>
																		</Stack>
																	</Stack>
																) : (
																	<>
																		<Text>
																			Please provide the full address details of
																			the hotel which will be used in vouchers.
																		</Text>
																		<AddressInputField name="address" />
																	</>
																)}
															</Stack>
														)}
													</Component>
												)}
											</Stack>
										</Col>
									</Grid>
									<Divider />
									<Grid gap="4">
										<Col sm={12} md={4}>
											<Stack gap="1">
												<Heading as="h3">Services</Heading>
												<Text color="muted">
													Please provide services details available in this
													hotel e.g. meal plans and rooms along with
													checkin/checkout times and child's age range for extra
													bed.
												</Text>
											</Stack>
										</Col>
										<Col>
											<Stack gap="4">
												<SelectField
													select={SelectMealPlans}
													name="meal_plans"
													creatable
													label="Meal Plans"
													fetchOnMount
													multiple
												/>

												<FieldArray name="room_types">
													{({ fields }) => (
														<Box marginY={quickAdd ? undefined : "8"}>
															{quickAdd ? null : (
																<Box marginBottom="4">
																	<Box fontWeight="semibold" fontSize="md">
																		Room Types with Allowed Extra
																		Bed(s)/Mattress(es)
																	</Box>
																	<Box color="muted">
																		Please select room types available in this
																		hotel along with allowed extra beds. Group
																		room types that have same number of allowed
																		extra beds
																	</Box>
																</Box>
															)}

															<Box
																as="ul"
																borderLeftWidth={
																	quickAdd && Number(fields.length) <= 1
																		? "0"
																		: "1"
																}
																className="list"
																style={
																	quickAdd && Number(fields.length) <= 1
																		? {
																				margin: 0,
																			}
																		: undefined
																}
															>
																{fields.map((name, index) => (
																	<Box
																		as="li"
																		key={name}
																		style={
																			quickAdd && Number(fields.length) <= 1
																				? {
																						padding: 0,
																					}
																				: undefined
																		}
																	>
																		<Grid
																			gap={
																				quickAdd && Number(fields.length) <= 1
																					? "0"
																					: "4"
																			}
																			noGutters
																		>
																			<Col xs={12} md={quickAdd ? 12 : 4}>
																				<SelectField
																					select={SelectRoomTypes}
																					name={`${name}.room_types`}
																					label="Room Types"
																					creatable
																					fetchOnMount
																					multiple
																				/>
																			</Col>
																			{quickAdd ? null : (
																				<>
																					<Col xs="auto">
																						<Stack gap="2">
																							<TextInputField
																								label="Allowed extra bed(s)"
																								type="number"
																								name={`${name}.allowed_extra_beds`}
																								min={0}
																								max={1000}
																								placeholder="e.g. 1"
																							/>
																							<Inline gap="2">
																								<TextInputField
																									label="AWEB(s)"
																									type="number"
																									name={`${name}.allowed_adults_with_extra_bed`}
																									min={0}
																									max={1000}
																									placeholder="e.g. 1"
																									size="sm"
																								/>
																								<TextInputField
																									label="CWEB(s)"
																									type="number"
																									name={`${name}.allowed_children_with_extra_bed`}
																									min={0}
																									max={1000}
																									placeholder="e.g. 1"
																									size="sm"
																								/>
																								<TextInputField
																									label="CNB(s)"
																									type="number"
																									name={`${name}.allowed_children_without_extra_bed`}
																									min={0}
																									max={1000}
																									placeholder="e.g. 0"
																									size="sm"
																								/>
																							</Inline>
																						</Stack>
																					</Col>
																					<Col xs="auto">
																						<TextInputField
																							label="No. of Rooms"
																							type="number"
																							name={`${name}.no_of_rooms`}
																							min={1}
																							max={1000}
																							placeholder="e.g. 25"
																						/>
																					</Col>
																					<Col xs="auto" paddingTop="6">
																						{Number(fields.length) > 1 ? (
																							<Button
																								onClick={() =>
																									fields.remove(index)
																								}
																								size="sm"
																								level="tertiary"
																							>
																								<Icons.Cancel /> Remove
																							</Button>
																						) : null}
																					</Col>
																				</>
																			)}
																		</Grid>
																	</Box>
																))}
																{quickAdd ? null : (
																	<Box as="li">
																		<Button
																			onClick={() =>
																				fields.push({
																					room_type: undefined,
																					allowed_extra_beds: 1,
																				})
																			}
																			size="sm"
																		>
																			<Icons.Plus /> Add Rooms with Different
																			Extra Beds
																		</Button>
																	</Box>
																)}
															</Box>
														</Box>
													)}
												</FieldArray>
											</Stack>
										</Col>
									</Grid>
									{quickAdd ? null : (
										<>
											<Divider />
											<GetFieldValue<
												{
													room_types: IRoomType[]
													allowed_extra_beds: number
													allowed_adults_with_extra_bed: number
													allowed_children_with_extra_bed: number
													allowed_children_without_extra_bed?: number
													no_of_rooms?: number
												}[]
											> name="room_types">
												{({ value: room_types }) => {
													const extractedRoomTypes = room_types.flatMap(
														(item) => item.room_types || []
													)
													return (
														<HotelBlackoutDateIntervalsField
															roomTypes={extractedRoomTypes}
														/>
													)
												}}
											</GetFieldValue>
										</>
									)}
									{quickAdd ? null : (
										<>
											<Divider />
											<Grid gap="4">
												<Col sm={12} md={4}>
													<Stack gap="1">
														<Heading as="h3">Configuration</Heading>
														<Text color="muted">
															Please provide the checkin/checkout time along
															with age of children for extra beds.
														</Text>
													</Stack>
												</Col>
												<Col>
													<Stack gap="4">
														<Grid marginBottom="6" gap="4">
															<Col>
																<DatePickerField
																	label="Checkin Time"
																	name="checkin_at"
																	dateFormat={false}
																	timeFormat="HH:mm"
																	required
																/>
															</Col>
															<Col>
																<DatePickerField
																	label="Checkout Time"
																	name="checkout_at"
																	dateFormat={false}
																	timeFormat="HH:mm"
																	required
																/>
															</Col>
														</Grid>
														<Stack gap="4">
															<Stack gap="1">
																<Box
																	fontWeight="semibold"
																	as="label"
																	htmlFor="extra_bed_child_age_start"
																	display="block"
																>
																	Children Age range for Charges on Child No Bed
																	/ Child Extra Bed/Mattress
																</Box>
																<Inline gap="4">
																	<TextInputField
																		name="extra_bed_child_age_start"
																		required
																		type="number"
																		min={1}
																		max={1000}
																	/>
																	<TextInputField
																		name="extra_bed_child_age_end"
																		required
																		type="number"
																		min={1}
																		max={1000}
																	/>
																</Inline>
															</Stack>
															<Box fontSize="sm">
																<Stack
																	as="ul"
																	listStyleType="disc"
																	color="muted"
																	marginLeft="4"
																	gap="1"
																>
																	<Box as="li">
																		Below this range, children are counted as{" "}
																		<b>complementary kids</b>.
																	</Box>
																	<Box as="li">
																		Above this range, children are counted as{" "}
																		<b>Adults</b>.
																	</Box>
																	<Box as="li">
																		Between this range, charges for{" "}
																		<b>no extra beds, extra beds or meal</b>{" "}
																		will be applicable
																	</Box>
																</Stack>
															</Box>
														</Stack>
													</Stack>
												</Col>
											</Grid>
										</>
									)}
								</>
							)}
						</GetFieldValue>

						<Divider />
						<GetFieldValue<boolean> name={`quick_add`}>
							{({ value: quickAdd }) =>
								quickAdd ? null : (
									<>
										<Grid gap="4">
											<Col sm={12} md={4}>
												<Stack gap="1">
													<Heading as="h3">Payment Preferences</Heading>
													<Text color="muted">
														Select / Create a payment preference for by which
														this hotel accepts the payments.
													</Text>
												</Stack>
											</Col>
											<Col>
												<SelectField
													select={SelectHotelPaymentPreferences}
													name="payment_preference"
													label="Payment Preference"
													secondaryLabel="optional"
													multiple={false}
													creatable
													fetchOnMount
												/>
											</Col>
										</Grid>
										<Divider />
										<Grid gap="4">
											<Col sm={12} md={4}>
												<Stack gap="1">
													<Heading as="h3">More Details</Heading>
													<Text color="muted">
														Attach more details such as hotel's details link
														etc.
													</Text>
												</Stack>
											</Col>
											<Col>
												<TextInputField
													name="url"
													label="Hotel Images / Details Link"
													secondaryLabel="optional"
													type="url"
													placeholder="e.g. https://www.hotelname.com/images"
												/>
											</Col>
										</Grid>
										<Divider />
									</>
								)
							}
						</GetFieldValue>

						<Grid>
							<Col sm={12} md={{ offset: 4, span: 8 }}>
								<Stack gap="4">
									<SubmissionError />
									<Inline gap="4">
										<Button type="submit" disabled={submitting} size="lg">
											{submitting ? "Please wait..." : "Save Hotel Details"}
										</Button>
										{onCancel ? (
											<Button
												onClick={onCancel}
												level="tertiary"
												size="lg"
												disabled={submitting}
											>
												Cancel
											</Button>
										) : null}
									</Inline>
								</Stack>
							</Col>
						</Grid>
					</form>
				)
			}}
		</Form>
	)
}

export function AddHotelDialog({
	open,
	onClose,
	onSuccess,
	...props
}: Omit<React.ComponentProps<typeof CreateHotel>, "onCancel"> & {
	open: boolean
	onClose: () => void
}) {
	return (
		<Dialog open={open} onClose={onClose} lg>
			<Dialog.Header closeButton>
				<Dialog.Title>Add new Hotel</Dialog.Title>
			</Dialog.Header>
			<Dialog.Body>
				<CreateHotel
					onSuccess={(hotel) => {
						onClose()
						onSuccess?.(hotel)
						showSnackbar(`Hotel ${hotel.name} successfully added.`)
					}}
					onCancel={onClose}
					{...props}
				/>
			</Dialog.Body>
		</Dialog>
	)
}
export function HotelClosedOnDateIntervalsField({
	name,
	lable,
	help,
}: {
	name: string
	lable: string
	help?: string
}) {
	const form = useForm()
	return (
		<Stack gap="1">
			<Text fontWeight="semibold">{lable}</Text>
			{help ? (
				<Text color="muted" fontSize="sm">
					{help}
				</Text>
			) : null}
			<FieldArray name={name}>
				{({ fields }) => {
					return (
						<Stack flexWrap="wrap" gap="2">
							{fields.map((itemName, index) => (
								<Inline key={index} gap="4">
									<Stack>
										<DateRangePickerField
											fromName={`${itemName}.start_date`}
											toName={`${itemName}.end_date`}
											rangePresets={null}
										/>
									</Stack>
									<Box>
										<Button
											level="tertiary"
											onClick={() => {
												fields.remove(index)
												if ((fields.length ?? 0) <= 1) {
													form.change(name, [])
												}
											}}
										>
											<Icons.Cancel />
										</Button>
									</Box>
								</Inline>
							))}
							<Box>
								<Button
									size={Number(fields.length || 0) >= 1 ? "sm" : undefined}
									onClick={() => {
										fields.push({ start_date: undefined, end_date: undefined })
									}}
								>
									<Icons.Plus />{" "}
									{!fields.length ? "Add Date Intervals" : "Add More"}
								</Button>
							</Box>
						</Stack>
					)
				}}
			</FieldArray>
		</Stack>
	)
}

export function HotelBlackoutDateIntervalsField({
	roomTypes,
}: {
	roomTypes: IRoomType[] | IHotelRoomType[]
}) {
	const rooms = Array.from(
		new Map(roomTypes.map((room) => [room.id, room])).values(),
		(rt) => ({
			name: rt.name,
			value: rt.id,
		})
	)
	const form = useForm()

	function handleToggleForBlackoutSoldoutChange(
		checked: 0 | 1,
		toggleFieldName: string,
		formFieldTypeForAllRooms: string,
		formFieldTypeForRoom: string
	) {
		const isChecked = Boolean(checked)
		form.change(toggleFieldName, isChecked)

		if (isChecked) {
			form.change(formFieldTypeForRoom, [])
		} else {
			form.change(formFieldTypeForRoom, [
				{ black_out_date_intervals: [], room_types: [] },
			])
			form.change(formFieldTypeForAllRooms, [])
		}
	}

	return (
		<Stack gap="6">
			{/* ---------- Soldout Dates section ---------- */}
			<Grid gap="4" paddingBottom="8" borderBottomWidth="1">
				{/* Left column: heading + description */}
				<Col sm={12} md={4}>
					<Stack gap="1">
						<Heading as="h4">Soldout Dates</Heading>
						<Stack as="ul" paddingTop="2" gap="1">
							<Box as="li" display="flex" gap="2" alignItems="start">
								<Text color="muted" fontSize="sm">
									•
								</Text>
								<Text color="muted" fontSize="sm">
									Block room sales when rooms are fully booked
								</Text>
							</Box>
							<Box as="li" display="flex" gap="2" alignItems="start">
								<Text color="muted" fontSize="sm">
									•
								</Text>
								<Text color="muted" fontSize="sm">
									Use for fully booked dates or temporarily unavailable rooms
								</Text>
							</Box>
							<Box as="li" display="flex" gap="2" alignItems="start">
								<Text color="muted" fontSize="sm">
									•
								</Text>
								<Text color="muted" fontSize="sm">
									Apply soldout dates to specific room types or all rooms
								</Text>
							</Box>
						</Stack>
					</Stack>
				</Col>

				{/* Right column: switch on top, interval content below — same left edge */}
				<Col>
					<Stack gap="2">
						<SwitchInputField
							name="same_sold_out_date_intervals_for_all_room_types"
							label="For all rooms"
							onChange={(checked) =>
								handleToggleForBlackoutSoldoutChange(
									checked,
									"same_sold_out_date_intervals_for_all_room_types",
									"stop_sale_date_intervals",
									"stop_sale_date_for_room_types"
								)
							}
						/>

						<GetFieldValue<boolean> name="same_sold_out_date_intervals_for_all_room_types">
							{({ value }) =>
								value ? (
									<SimpleDateIntervalsField
										name="stop_sale_date_intervals"
										dateColumnLabel="Soldout date intervals"
									/>
								) : (
									<RoomTypeDateIntervalRestrictionsInputField
										dateColumnLabel="Soldout date intervals"
										addButtonLabel="Add Soldout Dates for Room Type"
										name="stop_sale_date_for_room_types"
										rooms={rooms}
									/>
								)
							}
						</GetFieldValue>
					</Stack>
				</Col>
			</Grid>

			{/* ---------- Blackout Dates section ---------- */}
			<Grid gap="4">
				{/* Left column: heading + description */}
				<Col sm={12} md={4}>
					<Stack gap="1">
						<Heading as="h4">Blackout Dates</Heading>
						<Stack as="ul" paddingTop="2" gap="1">
							<Box as="li" display="flex" gap="2" alignItems="start">
								<Text color="muted" fontSize="sm">
									•
								</Text>
								<Text color="muted" fontSize="sm">
									Get a warning for blackout dates and enter the applicable
									manual rate before confirming the quotation or booking.
								</Text>
							</Box>
							<Box as="li" display="flex" gap="2" alignItems="start">
								<Text color="muted" fontSize="sm">
									•
								</Text>
								<Text color="muted" fontSize="sm">
									Use for maintenance, private events, renovations
								</Text>
							</Box>
						</Stack>
					</Stack>
				</Col>

				{/* Right column: switch on top, interval content below — same left edge */}
				<Col>
					<Stack gap="2">
						<SwitchInputField
							name="same_black_out_date_intervals_for_all_room_types"
							label="For all rooms"
							onChange={(checked) =>
								handleToggleForBlackoutSoldoutChange(
									checked,
									"same_black_out_date_intervals_for_all_room_types",
									"blackout_date_intervals",
									"blackout_date_for_room_types"
								)
							}
						/>

						<GetFieldValue<boolean> name="same_black_out_date_intervals_for_all_room_types">
							{({ value }) =>
								value ? (
									<SimpleDateIntervalsField
										name="blackout_date_intervals"
										dateColumnLabel="Blackout date intervals"
									/>
								) : (
									<RoomTypeDateIntervalRestrictionsInputField
										name="blackout_date_for_room_types"
										dateColumnLabel="Blackout date intervals"
										addButtonLabel="Add Blackout Dates for Room Type"
										rooms={rooms}
									/>
								)
							}
						</GetFieldValue>
					</Stack>
				</Col>
			</Grid>
		</Stack>
	)
}

// Simplified — no longer owns the heading, just renders the field
function SimpleDateIntervalsField({
	name,
	dateColumnLabel,
}: {
	name: string
	dateColumnLabel: string
}) {
	return <HotelClosedOnDateIntervalsField lable={dateColumnLabel} name={name} />
}

// Simplified — no heading Col anymore, uses full width, cleaner row alignment
export type ClosedOnDateForRoomTypeItem = {
	black_out_date_intervals: Array<{ start_date?: Date; end_date?: Date }>
	room_types: { name: string; value: number }[]
}
function RoomTypeDateIntervalRestrictionsInputField({
	name,
	dateColumnLabel,
	addButtonLabel,
	rooms,
}: {
	name: string
	dateColumnLabel: string
	addButtonLabel: string
	rooms: { name: string; value: number }[]
}) {
	return (
		<Box as="ul" borderLeftWidth="1">
			<FieldArray<ClosedOnDateForRoomTypeItem> name={name}>
				{({ fields }) => (
					<>
						{fields.map((fieldName, index) => (
							<Box key={fieldName} as="li">
								<Box
									paddingX="4"
									paddingBottom="4"
									paddingTop={index === 0 ? "0" : "4"}
									borderBottomWidth="1"
								>
									<Grid gap="4" alignItems="start">
										<Col xs={12} md={4}>
											<SelectField
												select={Select}
												options={rooms}
												name={`${fieldName}.room_types`}
												label="Room Types"
												placeholder="Type to search..."
												multiple
											/>
										</Col>

										<Col xs={12} md={6}>
											<HotelClosedOnDateIntervalsField
												lable={dateColumnLabel}
												name={`${fieldName}.black_out_date_intervals`}
											/>
										</Col>
									</Grid>

									<Stack width="full" paddingTop="1" alignItems="end">
										<Button
											type="button"
											onClick={() => fields.remove(index)}
											size="sm"
											level="secondary"
										>
											<Icons.Cancel /> Remove
										</Button>
									</Stack>
								</Box>
							</Box>
						))}
						<Box paddingTop="4" paddingLeft="4" paddingBottom="4">
							<Button
								type="button"
								onClick={() =>
									fields.push({
										room_types: [],
										black_out_date_intervals: [],
									})
								}
								size="base"
							>
								<Icons.Plus /> {addButtonLabel}
							</Button>
						</Box>
					</>
				)}
			</FieldArray>
		</Box>
	)
}
